import classNames from 'classnames'
import React, { useMemo } from 'react'
import './styles.scss'

export interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  showInfo?: boolean
  showQuickJump?: boolean
  maxVisiblePages?: number
  className?: string
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true,
  showQuickJump = false,
  maxVisiblePages = 7,
  className,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  // Calculate visible page numbers
  const visiblePages = useMemo(() => {
    const pages: number[] = []
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Calculate range around current page
      const halfVisible = Math.floor(maxVisiblePages / 2)
      let start = Math.max(1, currentPage - halfVisible)
      let end = Math.min(totalPages, currentPage + halfVisible)
      
      // Adjust if we're near the beginning or end
      if (end - start + 1 < maxVisiblePages) {
        if (start === 1) {
          end = Math.min(totalPages, start + maxVisiblePages - 1)
        } else if (end === totalPages) {
          start = Math.max(1, end - maxVisiblePages + 1)
        }
      }
      
      // Add first page and ellipsis if needed
      if (start > 1) {
        pages.push(1)
        if (start > 2) {
          pages.push(-1) // -1 represents ellipsis
        }
      }
      
      // Add visible pages
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      // Add last page and ellipsis if needed
      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(-1) // -1 represents ellipsis
        }
        pages.push(totalPages)
      }
    }
    
    return pages
  }, [currentPage, totalPages, maxVisiblePages])

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page)
    }
  }

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, page: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handlePageClick(page)
    }
  }

  if (totalPages <= 1) {
    return null
  }

  return (
    <div className={classNames('pagination', className)}>
      {showInfo && (
        <div className="pagination__info">
          Showing {startItem.toLocaleString()} to {endItem.toLocaleString()} of {totalItems.toLocaleString()} results
        </div>
      )}
      
      <div className="pagination__controls">
        {/* Previous Button */}
        <button
          className={classNames('pagination__button', 'pagination__button--prev', {
            'pagination__button--disabled': currentPage === 1,
          })}
          onClick={handlePrevious}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
          </svg>
          Previous
        </button>

        {/* Page Numbers */}
        <div className="pagination__pages">
          {visiblePages.map((page, index) => {
            if (page === -1) {
              // Ellipsis
              return (
                <span key={`ellipsis-${index}`} className="pagination__ellipsis">
                  ...
                </span>
              )
            }
            
            return (
              <button
                key={page}
                className={classNames('pagination__button', 'pagination__button--page', {
                  'pagination__button--current': page === currentPage,
                })}
                onClick={() => handlePageClick(page)}
                onKeyDown={(e) => handleKeyDown(e, page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          })}
        </div>

        {/* Next Button */}
        <button
          className={classNames('pagination__button', 'pagination__button--next', {
            'pagination__button--disabled': currentPage === totalPages,
          })}
          onClick={handleNext}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          Next
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
      </div>
      
      {showQuickJump && totalPages > 10 && (
        <div className="pagination__quick-jump">
          <label htmlFor="page-jump" className="pagination__quick-jump-label">
            Go to page:
          </label>
          <input
            id="page-jump"
            type="number"
            min="1"
            max={totalPages}
            className="pagination__quick-jump-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt((e.target as HTMLInputElement).value, 10)
                if (page >= 1 && page <= totalPages) {
                  handlePageClick(page)
                }
              }
            }}
            placeholder="1"
          />
        </div>
      )}
    </div>
  )
}